import { Section } from "views/applications-id-report/components/Section";
import VerifiedIcon from "shared/icons/verified-icon.svg?react";
import { Stat } from "views/applications-id-report/components/Stat";
import { VerifiedIncomeReport } from "types/landlord/reports";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { formatToCurrency } from "utils/currency";
import { VerifiedIncomeGraph } from "./VerifiedIncomeGraph";
import { useMemo } from "react";
import { VerifiedIncomeChangeInfoBanner } from "../components/VerifiedIncomeChangeInfoBanner";
import { useAuthContext } from "providers/Auth";

type IncomeChangeLog = {
  created_at: string;
  field: string;
  from: string;
  to: string;
  changedBy: string;
};
interface VerifiedIncomeSummaryProps {
  report?: VerifiedIncomeReport;
  setShowEditIncomeModal: (show: boolean) => void;
}
export const VerifiedIncomeSummary = ({
  report,
  setShowEditIncomeModal
}: VerifiedIncomeSummaryProps) => {
  const { type, permissions } = useAuthContext();
  const {
    document,
    gross_monthly_income,
    net_monthly_income,
    debt_income_ratio,
    bank_income,
    items,
    income_count,
    net_income_summary,
    income_source_type,
    forecasted_net_monthly_income,
    notes
  } = report || {};

  const verifiedIncomeChanges = useMemo(() => {
    const incomeChanges = notes?.reduce<{
      grossMonthlyIncomeChanges: IncomeChangeLog[];
      netMonthlyIncomeChanges: IncomeChangeLog[];
    }>(
      (acc, note) => {
        const changes = note.attachment?.changes || [];

        changes.map((change) => {
          if (change.field === "gross_monthly_income")
            acc.grossMonthlyIncomeChanges.push({
              ...change,
              created_at: note.created_at,
              changedBy: note.attachment?.changed_by?.name || ""
            });
          else if (change.field === "net_monthly_income")
            acc.netMonthlyIncomeChanges.push({
              ...change,
              created_at: note.created_at,
              changedBy: note.attachment?.changed_by?.name || ""
            });
        });

        return acc;
      },
      { grossMonthlyIncomeChanges: [], netMonthlyIncomeChanges: [] }
    );

    const { grossMonthlyIncomeChanges, netMonthlyIncomeChanges } =
      incomeChanges || {};
    return {
      netMonthlyIncomeChanges: netMonthlyIncomeChanges?.length
        ? {
            originalValue:
              netMonthlyIncomeChanges[netMonthlyIncomeChanges.length - 1]?.from,
            date: netMonthlyIncomeChanges[0]?.created_at,
            changedBy: netMonthlyIncomeChanges[0]?.changedBy
          }
        : null,
      grossMonthlyIncomeChanges: grossMonthlyIncomeChanges?.length
        ? {
            originalValue:
              grossMonthlyIncomeChanges[grossMonthlyIncomeChanges.length - 1]
                ?.from,
            date: grossMonthlyIncomeChanges[0]?.created_at,
            changedBy: grossMonthlyIncomeChanges[0]?.changedBy
          }
        : null
    };
  }, [notes]);
  return (
    <Section
      maxWidth="none"
      sectionTitle="Verified income summary"
      icon={<VerifiedIcon />}
      rightElement={
        <Show
          if={Boolean(
            type.isAdmin || type.isOwner || permissions.canEditIncome
          )}
        >
          <Button
            variant={ButtonVariant.TEXT}
            size={ButtonSize.XS}
            onClick={() => setShowEditIncomeModal(true)}
            className="no-print"
          >
            Edit income
          </Button>
        </Show>
      }
    >
      <Show if={Boolean(verifiedIncomeChanges.grossMonthlyIncomeChanges)}>
        <VerifiedIncomeChangeInfoBanner
          title="Gross monthly income"
          changes={verifiedIncomeChanges.grossMonthlyIncomeChanges}
        />
      </Show>
      <Show if={Boolean(verifiedIncomeChanges.netMonthlyIncomeChanges)}>
        <VerifiedIncomeChangeInfoBanner
          title="Net monthly income"
          changes={verifiedIncomeChanges.netMonthlyIncomeChanges}
        />
      </Show>
      <div className="grid md:grid-cols-3 gap-4 pb-8 border-b border-alpha-100">
        <Stat
          title="Gross monthly income"
          value={
            gross_monthly_income
              ? formatToCurrency({
                  cents: gross_monthly_income.cents,
                  currency: gross_monthly_income.currency
                })
              : "-"
          }
        />
        <Stat
          title="Net monthly income"
          value={
            net_monthly_income
              ? formatToCurrency({
                  cents: net_monthly_income.cents,
                  currency: net_monthly_income.currency
                })
              : "-"
          }
        />
        <Stat
          title="Debt to income ratio"
          value={debt_income_ratio ? `${debt_income_ratio}%` : "-"}
        />
      </div>

      {/* This is only for bank */}
      <div className="grid md:grid-cols-3 gap-4 py-8 border-b border-alpha-100">
        <Stat
          title="Forecasted net monthly income"
          value={
            forecasted_net_monthly_income
              ? formatToCurrency(forecasted_net_monthly_income)
              : "-"
          }
        />
        <Stat
          title="Income sources"
          value={income_count ? String(income_count) : "-"}
        />
      </div>
      <div className="pt-6">
        <div className="flex justify-between mb-4">
          <p className="text-content-700 font-medium text-xs">
            Net income analysis
          </p>
        </div>
        <VerifiedIncomeGraph
          net_income_summary={net_income_summary}
          bankIncome={bank_income}
          document={document}
          items={items}
          incomeSourceType={
            income_source_type as "document" | "bank" | "payroll"
          }
        />
      </div>
    </Section>
  );
};
